import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, SlidersHorizontal, Grid3X3, List, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ListingCard from '../components/ListingCard';
import { api } from '../App';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CATEGORIES, SUBCATEGORIES, CONDITIONS, GRADING_COMPANIES, isCardCategory } from '../lib/categories';

const ALL_CATEGORIES = [{ value: 'all', label: 'All Categories' }, ...CATEGORIES];
const ALL_CONDITIONS = [{ value: 'all', label: 'Any Condition' }, ...CONDITIONS];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
];

export default function MarketplacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // Filter states
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [activeSearch, setActiveSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [listingType, setListingType] = useState(searchParams.get('type') || 'all');
  const [condition, setCondition] = useState(searchParams.get('condition') || 'all');
  const [minPrice, setMinPrice] = useState(searchParams.get('min_price') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('max_price') || '');
  const [tradeEnabled, setTradeEnabled] = useState(searchParams.get('trade') === 'true');
  const [sort, setSort] = useState(searchParams.get('sort') || 'newest');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [subcategory, setSubcategory] = useState(searchParams.get('subcategory') || 'all');
  const [isGraded, setIsGraded] = useState(searchParams.get('graded') || 'all');
  const [gradingCompany, setGradingCompany] = useState(searchParams.get('grader') || 'all');

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') params.append('category', category);
      if (subcategory !== 'all') params.append('subcategory', subcategory);
      if (listingType !== 'all') params.append('listing_type', listingType);
      if (condition !== 'all') params.append('condition', condition);
      if (activeSearch) params.append('search', activeSearch);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);
      if (tradeEnabled) params.append('trade_enabled', 'true');
      if (isGraded === 'yes') params.append('is_graded', 'true');
      if (isGraded === 'no') params.append('is_graded', 'false');
      if (gradingCompany !== 'all') params.append('grading_company', gradingCompany);
      params.append('sort', sort);
      params.append('page', page);
      params.append('limit', 20);

      const { data } = await api.get(`/listings?${params.toString()}`);
      setListings(data.listings);
      setTotalPages(data.pages);
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [category, subcategory, listingType, condition, activeSearch, minPrice, maxPrice, tradeEnabled, isGraded, gradingCompany, sort, page]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setActiveSearch('');
    setCategory('all');
    setSubcategory('all');
    setListingType('all');
    setCondition('all');
    setMinPrice('');
    setMaxPrice('');
    setTradeEnabled(false);
    setIsGraded('all');
    setGradingCompany('all');
    setSort('newest');
    setPage(1);
  };

  const hasActiveFilters = category !== 'all' || subcategory !== 'all' || listingType !== 'all' || condition !== 'all' || 
                           minPrice || maxPrice || tradeEnabled || activeSearch || isGraded !== 'all' || gradingCompany !== 'all';
  
  const showCardFilters = isCardCategory(category);

  return (
    <div className="min-h-screen bg-surface-base">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-2">
            Marketplace
          </h1>
          <p className="text-zinc-400">
            Discover rare coins, precious metals, collectibles, and more
          </p>
        </div>

        {/* Search & Filters Bar */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search listings..."
              data-testid="marketplace-search-input"
              className="input-dark w-full pl-12 pr-4 py-3"
            />
          </form>

          <div className="flex gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
              className={`btn-secondary px-4 py-3 flex items-center gap-2 ${showFilters ? 'border-gold text-gold' : ''}`}
            >
              <SlidersHorizontal className="w-5 h-5" />
              <span className="hidden sm:inline">Filters</span>
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-gold" />
              )}
            </button>

            <div className="hidden sm:flex border border-border-subtle">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-3 ${viewMode === 'grid' ? 'bg-surface-card text-gold' : 'text-zinc-400'}`}
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-3 ${viewMode === 'list' ? 'bg-surface-card text-gold' : 'text-zinc-400'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>

            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[180px] input-dark border-border-subtle">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-surface-card border-border-subtle">
                {SORT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mb-8 p-6 bg-surface-card border border-border-subtle animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-clash text-lg font-medium text-white">Filters</h3>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-sm text-gold hover:text-gold-hover flex items-center gap-1">
                  <X className="w-4 h-4" /> Clear all
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Category</label>
                <Select value={category} onValueChange={(v) => { setCategory(v); setSubcategory('all'); }}>
                  <SelectTrigger className="w-full input-dark border-border-subtle" data-testid="category-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface-card border-border-subtle">
                    {ALL_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {SUBCATEGORIES[category] && (
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Subcategory</label>
                  <Select value={subcategory} onValueChange={setSubcategory}>
                    <SelectTrigger className="w-full input-dark border-border-subtle" data-testid="subcategory-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-card border-border-subtle">
                      <SelectItem value="all">All</SelectItem>
                      {SUBCATEGORIES[category].map(sub => (
                        <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Listing Type</label>
                <Select value={listingType} onValueChange={setListingType}>
                  <SelectTrigger className="w-full input-dark border-border-subtle" data-testid="type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface-card border-border-subtle">
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="buy_now">Buy Now</SelectItem>
                    <SelectItem value="auction">Auction</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Condition</label>
                <Select value={condition} onValueChange={setCondition}>
                  <SelectTrigger className="w-full input-dark border-border-subtle" data-testid="condition-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface-card border-border-subtle">
                    {ALL_CONDITIONS.map(cond => (
                      <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Price Range</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    placeholder="Min"
                    className="input-dark w-full px-3 py-2"
                  />
                  <input
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    placeholder="Max"
                    className="input-dark w-full px-3 py-2"
                  />
                </div>
              </div>

              {showCardFilters && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Graded</label>
                    <Select value={isGraded} onValueChange={setIsGraded}>
                      <SelectTrigger className="w-full input-dark border-border-subtle" data-testid="graded-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-surface-card border-border-subtle">
                        <SelectItem value="all">Any</SelectItem>
                        <SelectItem value="yes">Graded Only</SelectItem>
                        <SelectItem value="no">Raw / Ungraded</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Grading Company</label>
                    <Select value={gradingCompany} onValueChange={setGradingCompany}>
                      <SelectTrigger className="w-full input-dark border-border-subtle" data-testid="grader-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-surface-card border-border-subtle">
                        <SelectItem value="all">Any</SelectItem>
                        {GRADING_COMPANIES.map(g => (
                          <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={tradeEnabled}
                  onChange={(e) => setTradeEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-border-subtle bg-surface-base text-gold"
                />
                <span className="text-sm text-zinc-300">Trade enabled only</span>
              </label>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1'}`}>
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square bg-surface-card animate-pulse" />
            ))}
          </div>
        ) : listings.length > 0 ? (
          <>
            <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1'}`}>
              {listings.map((listing, idx) => (
                <ListingCard key={listing.id} listing={listing} index={idx} viewMode={viewMode} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary px-4 py-2 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-zinc-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary px-4 py-2 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 bg-surface-card flex items-center justify-center">
              <Search className="w-10 h-10 text-zinc-600" />
            </div>
            <h3 className="font-clash text-xl font-medium text-white mb-2">No listings found</h3>
            <p className="text-zinc-400 mb-6">Try adjusting your filters or search terms</p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="btn-gold px-6 py-3">
                Clear Filters
              </button>
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
